// Copyright (C) Microsoft. All rights reserved.
import { ResourceKeys } from "./keys";
import { resourceLanguages } from "./languages";
/**
 * Repo-neutral component to load localizable strings defined in ./resources.json file.
 */
export class ResourceLoader {
    constructor(resourceToInject) {
        this.language = "en-us";
        this.oneFilePerLanguage = false;
        this.resources = null;
        this.loadedPromiseResolver = null;
        this.loadedPromiseRejector = null;
        this.languageNotFoundCallback = (bcp47Name) => { }; // eslint-disable-line no-empty, @typescript-eslint/no-empty-function
        this.loadedPromise = new Promise((resolve, reject) => {
            this.loadedPromiseResolver = resolve;
            this.loadedPromiseRejector = reject;
        });
        if (resourceToInject) {
            this.resources = this.ensureEmptyStringOnLackingResource(resourceToInject);
        }
    }
    /**
     * Inject strings as resources so that the tests can cut the dependency to the localization.
     * @param resources Mock data of the localizable strings
     */
    static inject(resources) {
        ResourceLoader.instance = new ResourceLoader(resources);
    }
    /**
     * @param options preventLoading: True if this needs to be called from the unit test. False otherwise.
     * @param options oneFilePerLanguage: True if each language has its own Javascript file for each language set up. False otherwise.
     * Be in sync with the option in the resource loader plugin initialization.
     * @param options languageNotFoundCallback: Function that is executed when the current user language is not found in the resource file.
     * @param options languageOverride: BCP47 language name to load the resources for. Browser default language is used if not specified.
     */
    initialize(options) {
        if (options.oneFilePerLanguage) {
            this.oneFilePerLanguage = options.oneFilePerLanguage;
        }
        if (options.languageNotFoundCallback) {
            this.languageNotFoundCallback = options.languageNotFoundCallback;
        } // else: keep using the default this.languageNotFoundCallback, which is an empty lambda.
        let language = options.languageOverride ? options.languageOverride : window.navigator.language;
        language = language.toLowerCase(); // avoid depending on the casing
        if (resourceLanguages.indexOf(language) >= 0) {
            // Note: ECMA 2016 defines Array.includes().
            this.language = language;
        }
        else {
            const fallbackLanguage = this.getFallbackLanguage(language);
            if (fallbackLanguage) {
                this.language = fallbackLanguage;
            }
            else {
                this.languageNotFoundCallback(language);
            }
        }
        if (!options.preventLoading) {
            this.startLoading();
        }
    }
    /** Get the JSON that contains the localized strings for the browser UI language.
     * The returned JSON could include empty string if the resource string is too new or the unit test does not mock all the localizable strings.
     */
    async getAsync() {
        if (this.resources) {
            return this.resources;
        }
        // else
        return this.loadedPromise;
    }
    get() {
        if (!this.resources) {
            throw new Error("Resources not loaded");
        }
        return this.resources;
    }
    // This method is called only once per page load in the user scenario. Better to free the data memory once it's used instead of statically hold them.
    getFallbackLanguage(language) {
        // Detail: https://microsoft.sharepoint.com/sites/globalreadiness/Lists/Language%20Fallbacks/GPWebView.aspx
        // Rule 1: based on the full BCP 47 names, in lower case.
        const languageFallbackMap = new Map([
            ["zh-hans-hk", "zh-cn"],
            ["zh-hans-mo", "zh-cn"],
            ["zh-hans", "zh-cn"],
            ["zh-sg", "zh-cn"],
            ["mn-mong", "ru-ru"],
            ["mn-mong-mn", "ru-ru"],
            ["mn-mong-cn", "zh-cn"],
            ["pt-mo", "en-us"],
            ["ro-md", "ru-ru"],
            ["smj-no", "nb-no"],
            ["smj-se", "sv-se"],
            ["se-fi", "fi-fi"],
            ["se-no", "nb-no"],
            ["se-se", "sv-se"],
            ["sma-no", "nb-no"],
            ["sma-se", "sv-se"],
            ["sr-cyrl", "sr-cyrl-rs"],
            ["sr-cyrl-xk", "sr-cyrl-rs"],
            ["sr-cyrl-me", "sr-cyrl-rs"],
            ["sr-latn", "sr-latn-rs"],
            ["sr-latn-ba", "sr-cyrl-ba"],
            ["sr-latn-xk", "sr-latn-rs"],
            ["sr-latn-me", "sr-latn-rs"],
            ["gsw-li", "de-de"],
            ["gsw-ch", "fr-fr"], // Swiss German (Switzerland) >> French (France)
        ]);
        // Rule 2: based on the primary language tag of the BCP 47 names, in lower case.
        const primaryLanguageFallbackMap = new Map([
            ["agq", "fr-fr"],
            ["gsw", "fr-fr"],
            ["ast", "es-es"],
            ["ksf", "fr-fr"],
            ["bm", "fr-fr"],
            ["bas", "fr-fr"],
            ["ba", "ru-ru"],
            ["eu", "es-es"],
            ["be", "ru-ru"],
            ["byn", "ar-sa"],
            ["br", "fr-fr"],
            ["tzm", "ar-sa"],
            ["ku", "ar-sa"],
            ["ce", "ru-ru"],
            ["cu", "ru-ru"],
            ["ksh", "de-de"],
            ["co", "fr-fr"],
            ["ewo", "fr-fr"],
            ["fo", "da-dk"],
            ["fr", "fr-fr"],
            ["fur", "it-it"],
            ["ff", "fr-fr"],
            ["gl", "es-es"],
            ["gn", "es-es"],
            ["jv", "id-id"],
            ["dyo", "fr-fr"],
            ["kea", "pt-pt"],
            ["kab", "fr-fr"],
            ["kkj", "fr-fr"],
            ["kl", "da-dk"],
            ["kk", "ru-ru"],
            ["quc", "es-es"],
            ["khq", "fr-fr"],
            ["ses", "fr-fr"],
            ["nmg", "fr-fr"],
            ["ky", "ru-ru"],
            ["lo", "fr-fr"],
            ["dsb", "de-de"],
            ["lu", "fr-fr"],
            ["lb", "de-de"],
            ["mgh", "pt-pt"],
            ["mg", "fr-fr"],
            ["arn", "es-es"],
            ["mzn", "fa-ir"],
            ["mgo", "fr-fr"],
            ["mfe", "fr-fr"],
            ["mua", "fr-fr"],
            ["nnh", "fr-fr"],
            ["jgo", "fr-fr"],
            ["nqo", "fr-fr"],
            ["oc", "fr-fr"],
            ["os", "ru-ru"],
            ["pap", "nl-nl"],
            ["pt", "pt-br"],
            ["quz", "es-es"],
            ["rn", "fr-fr"],
            ["ru", "ru-ru"],
            ["ssy", "ar-sa"],
            ["sah", "ru-ru"],
            ["smn", "fi-fi"],
            ["sms", "fi-fi"],
            ["sg", "fr-fr"],
            ["seh", "pt-pt"],
            ["es", "es-es"],
            ["zgh", "ar-sa"],
            ["sv", "sv-se"],
            ["syr", "ar-sa"],
            ["shi", "ar-sa"],
            ["tg", "ru-ru"],
            ["twq", "fr-fr"],
            ["tt", "ru-ru"],
            ["bo", "zh-cn"],
            ["tig", "ar-sa"],
            ["tk", "ru-ru"],
            ["hsb", "de-de"],
            ["ug", "zh-cn"],
            ["fy-nl", "nl-nl"],
            ["wo", "fr-fr"],
            ["yav", "fr-fr"],
            ["ii", "zh-cn"],
            ["dje", "fr-fr"], // Zarma >> French (France)
        ]);
        // Rule 3: if the resource language list includes only one language for a specific primary language, fall back to the existing one.
        // Below list includes those primary languages that should NOT use the rule.
        const primaryLanguageSingleLanguageFallbackDisablingList = [
            "bn",
            "zh",
            "el",
            "pt",
            "sr",
            "tr",
            "yi", // Yiddish
        ];
        // Rule 4: Fall back to English (U.S.)
        let candidate = language;
        const primaryLanguageSingleLanguageFallbackMap = new Map();
        while (resourceLanguages.indexOf(candidate) < 0) {
            // Rule 1
            const fallbackFromLanguage = languageFallbackMap.get(candidate);
            if (fallbackFromLanguage) {
                candidate = fallbackFromLanguage;
                continue;
            }
            // else: Rule 2
            const candidateTags = candidate.split("-");
            if (candidateTags.length > 0) {
                const candidatePrimaryLanguage = candidateTags[0];
                const fallbackFromPrimaryTag = primaryLanguageFallbackMap.get(candidatePrimaryLanguage);
                if (fallbackFromPrimaryTag) {
                    candidate = fallbackFromPrimaryTag;
                    continue;
                }
                // else: Rule 3
                if (primaryLanguageSingleLanguageFallbackMap.size === 0) {
                    // Build the rule dynamically
                    for (const resourceLanguage of resourceLanguages) {
                        const resourceLanguageTags = resourceLanguage.split("-");
                        if (resourceLanguageTags.length > 0) {
                            const resourceLanguagePrimaryTag = resourceLanguageTags[0];
                            if (primaryLanguageSingleLanguageFallbackMap.has(resourceLanguagePrimaryTag)) {
                                // Two or more resource langauges share the same primary language.
                                primaryLanguageSingleLanguageFallbackMap.set(resourceLanguagePrimaryTag, "");
                            }
                            else if (primaryLanguageSingleLanguageFallbackDisablingList.indexOf(resourceLanguagePrimaryTag) < 0) {
                                primaryLanguageSingleLanguageFallbackMap.set(resourceLanguagePrimaryTag, resourceLanguage);
                            }
                            // else: Rule 3 is disabled for the resource primary language.
                        }
                    }
                }
                const fallbackFromPrimaryLanguageSingleLanguage = primaryLanguageSingleLanguageFallbackMap.get(candidatePrimaryLanguage);
                if (fallbackFromPrimaryLanguageSingleLanguage) {
                    candidate = fallbackFromPrimaryLanguageSingleLanguage;
                    continue;
                }
            }
            // else: Rule 4
            // Return undefined to determine when fallback wasn't found and keep the default language of "en-us"
            return undefined;
        }
        return candidate;
    }
    startLoading() {
        const resourceFileUri = this.oneFilePerLanguage ? `./${this.language}/resources.json` : "./resources.json";
        // Prefering XHR to Fetch API as a part of repo-neutral component.
        const xhr = new XMLHttpRequest();
        xhr.responseType = "json"; // By setting this, xhr.response will be filled with the json-parsed oject.
        xhr.onreadystatechange = this.onReadyStateChange.bind(this, xhr);
        xhr.open("GET", resourceFileUri, true);
        xhr.send();
    }
    onReadyStateChange(xhr) {
        switch (xhr.readyState) {
            case XMLHttpRequest.DONE:
                const responseObject = xhr.response;
                if (responseObject && xhr.status < 300) {
                    // Ref) https://developer.mozilla.org/en-US/docs/Web/HTTP/Status
                    this.onLoaded(responseObject);
                }
                else {
                    this.onLoadError(new ErrorEvent(`onReadyStateChange: ${xhr.status}, ${this.language}`));
                }
                break;
        }
    }
    onLoaded(resources) {
        let resourcesForLanguage = {};
        if (this.oneFilePerLanguage) {
            resourcesForLanguage = resources;
        }
        else if (resources.hasOwnProperty(this.language)) {
            resourcesForLanguage = resources[this.language];
        }
        else {
            this.languageNotFoundCallback(this.language);
        }
        this.resources = this.ensureEmptyStringOnLackingResource(resourcesForLanguage);
        if (this.loadedPromiseResolver) {
            this.loadedPromiseResolver(this.resources);
        }
    }
    onLoadError(evernt) {
        if (this.loadedPromiseRejector) {
            this.loadedPromiseRejector(event);
        }
    }
    ensureEmptyStringOnLackingResource(resources) {
        let result = resources || {};
        for (const key in ResourceKeys) {
            if (!result.hasOwnProperty(key)) {
                result[key] = ""; // Either the string is too new or the UT is injecting strings lazily.
            }
        }
        return result;
    }
}
ResourceLoader.instance = new ResourceLoader();
